/**
 * Build Tab - Network Builder Interface
 * Wraps the existing MainApp chat/build logic.
 *
 * Historical Brain runs now render in the Workbench "Executions" tab. This
 * component still fetches execution rows so pending approvals can be
 * rehydrated after refresh or network switch.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Sparkles, Paperclip, Wand2 } from 'lucide-react';
import ComposerPlusMenu from '@stepflow/ui-shared/components/ComposerPlusMenu.jsx';
import { getRestApiBearerToken } from '@/services/apiToken';
import { refreshNetworkPrompts } from '@stepflow/ui-shared/services/brainBuildApi.js';
import ChatWorkspace from './ChatWorkspace.jsx';
import GraphVisualization from './GraphVisualization.jsx';
import ManifestTreeFlow from './ManifestTreeFlow.jsx';
import { useAppStore } from '@/stores/useAppStore';
import { DEFAULT_VECTOR_DB_PROVIDER } from '@stepflow/ui-shared/constants/vectorDbProvider.js';
import { useBrainSession } from '@stepflow/ui-shared/hooks/useBrainSession.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/**
 * BuildTab is the **Brain** domain surface (see
 * ``doc/arch/vertical/NETWORK-TO-ROOMS.md``). Brain SFN history lives here in local
 * component state sourced from ``GET /networks/{id}/executions``; it must
 * not read from the ``useAppStore.executions`` registry — that registry is
 * scoped to Runtime SFN executions (Run tab / Agentic Network) and mixing
 * the two produced the "lots of blank executions across networks" bug.
 */

/** Brain-task statuses that belong in "Execution History" only — completed runs, not in-flight work. */
const EXECUTION_HISTORY_TERMINAL_STATUSES = new Set(['SUCCESS', 'FAILED']);

export default function BuildTab({
  network,
  selectedNetworkId,
  onTriggerStartDeployment,
  onNetworkUpdate,
  // One-shot revise prompt from the Self-Improvement tab (§6b). Prefills the composer
  // + flips Generative on (an improve-revise is inherently a Brain2 re-design).
  seedPrompt = '',
}) {
  const { connectionId: storeConnectionId } = useAppStore();

  // ─── Brain session state (reducer-based) ──────────────────────────────
  const {
    messages,
    stages,
    activeStage,
    artifact,
    isProcessing,
    buildActivity,
    inputState,
    approvalState,
    approvalDecisions,
    approvalLoadError,
    pendingTaskIdForRetry,
    currentConnectionId,
    showApprovalBar,
    inputDisabled,
    dispatch,
    isCreatingNetworkRef,
    sendMessage,
    restoreApproval,
    clearApproval,
    recordApprovalDecision,
    setApprovalLoadError,
    setPendingTaskForRetry,
  } = useBrainSession({
    network,
    selectedNetworkId,
    storeConnectionId,
    onComplete: () => {
      if (onNetworkUpdate) {
        onNetworkUpdate({ status: 'ACTIVE', _brainCompleted: true });
      }
    },
  });

  // ─── REST-fetched state (not event-sourced) ───────────────────────────
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);

  // Architect skill kick-start toggle (doc/arch/vertical/SKILLS.md step 4).
  // Default ON; the user opts out here before the first build. Sent to the
  // Brain SFN so prompt_engineer drafts NETWORK-scoped skills from the
  // requirement doc (no-op for thin prompts).
  const [generateNetworkSkills, setGenerateNetworkSkills] = useState(true);

  // "+" ADVANCED / GENERATIVE toggle (GENERATIVE-NETWORKS.md §3a). Default ON → Brain2
  // (manifest architect + the recursive engine) is now the default build path. Turn OFF
  // to fall back to the Simple brain (ASL). Purely a routing flag on start-deployment.
  const [generative, setGenerative] = useState(true);

  // A revise prompt from the Improve tab arrives → this network already IS generative
  // (only manifest networks have suggestions), so turn Generative on for the send.
  useEffect(() => {
    if (String(seedPrompt || '').trim()) setGenerative(true);
  }, [seedPrompt]);

  const chatInputRef = useRef(null);

  // ─── Diagram panel width (user-draggable left edge) ───────────────────
  const DIAGRAM_MIN = 280;
  const DIAGRAM_MAX = 760;
  const [diagramWidth, setDiagramWidth] = useState(480);
  const diagramDragRef = useRef(null);
  const onDiagramResizeStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = diagramDragRef.current ?? diagramWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const move = (ev) => {
      // Dragging the LEFT edge: moving left (negative dx) widens the panel.
      const next = Math.min(DIAGRAM_MAX, Math.max(DIAGRAM_MIN, startW - (ev.clientX - startX)));
      diagramDragRef.current = next;
      setDiagramWidth(next);
    };
    const up = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [diagramWidth]);

  // ─── Fetch execution history ──────────────────────────────────────────
  useEffect(() => {
    if (!selectedNetworkId || selectedNetworkId === 'new') return undefined;
    const abort = new AbortController();
    fetchExecutions(selectedNetworkId, { signal: abort.signal });
    return () => { abort.abort(); };
  }, [selectedNetworkId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchExecutions = async (networkId, { signal } = {}) => {
    if (!networkId || networkId === 'new') return;

    setExecutionsLoading(true);
    try {
      const idToken = getRestApiBearerToken();
      const response = await fetch(`${API_BASE_URL}/networks/${networkId}/executions`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) return;
      const data = await response.json();
      const raw = data.executions || [];

      const networkStatus = String(network?.status || '').toUpperCase();
      const networkIsDeployed = networkStatus === 'ACTIVE' || networkStatus === 'RELEASED';
      const lastGoodTaskId = network?.lastSuccessfulTaskId || null;

      const rawPending = raw.find((e) => e.status === 'AWAITING_APPROVAL');
      const pending = (() => {
        if (!rawPending) return null;
        if (networkIsDeployed) return null;
        if (lastGoodTaskId && rawPending.taskId === lastGoodTaskId) return null;
        return rawPending;
      })();

      const historyOnly = raw
        .filter((e) => EXECUTION_HISTORY_TERMINAL_STATUSES.has(e.status))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setExecutions(historyOnly);

      if (pending) {
        setApprovalLoadError(false);
        setPendingTaskForRetry(pending.taskId);
        try {
          const detailResp = await fetch(
            `${API_BASE_URL}/networks/${networkId}/executions/${pending.taskId}`,
            {
              headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
              signal,
            },
          );
          if (detailResp.ok) {
            const details = await detailResp.json();
            if (details?.status === 'AWAITING_APPROVAL' && details.taskToken) {
              restoreApproval(details);
              setApprovalLoadError(false);
            } else {
              setApprovalLoadError(true);
            }
          } else {
            setApprovalLoadError(true);
          }
        } catch (e) {
          if (e?.name === 'AbortError') return;
          setApprovalLoadError(true);
        }
      } else {
        setPendingTaskForRetry(null);
        setApprovalLoadError(false);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error fetching executions:', error);
    } finally {
      setExecutionsLoading(false);
    }
  };

  const fetchExecutionDetails = async (taskId) => {
    try {
      const idToken = getRestApiBearerToken();
      const response = await fetch(`${API_BASE_URL}/networks/${network.networkId}/executions/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data?.status === 'AWAITING_APPROVAL' && data.taskToken) {
          restoreApproval(data);
        }
        return data;
      }
    } catch (error) {
      console.error('Error fetching execution details:', error);
    }
    return null;
  };

  // ─── Send message (create network or append) ──────────────────────────

  const handleSendMessage = async (rawMessage) => {
    // Honor the user's prompt VERBATIM — keep their newlines, bullets, and
    // indentation. We only normalize the NBSP (\xa0) that contenteditable's
    // innerText injects for leading spaces, normalize CRLF, and trim the outer
    // edges. We must NOT collapse internal whitespace (that flattened bullet
    // lists into one blob). Dedup downstream compares a whitespace-normalized
    // form separately, so storing the original is still double-persist safe.
    const message = String(rawMessage || '')
      .replace(/\u00a0/g, ' ')  // NBSP → space
      .replace(/\r\n/g, '\n')    // CRLF → LF
      .replace(/[ \t]+$/gm, '')  // strip trailing spaces per line
      .trim();                   // trim outer blank lines/spaces only
    if (!message) return;

    if (approvalState.awaitingFeedback && approvalState.taskToken) {
      dispatch({ type: 'client/SEND_MESSAGE', payload: { id: crypto.randomUUID(), content: message, timestamp: new Date().toISOString() } });
      await handleApprovalDecision('MODIFY', approvalState.taskToken, message);
      return;
    }

    // Show prompt + Processing spinner immediately — before any API call
    sendMessage(message, { replace: !network || network.networkId === 'new' });

    if (!network || network.networkId === 'new') {
      try {
        const idToken = getRestApiBearerToken();
        const response = await fetch(`${API_BASE_URL}/networks`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt: message,
            vectorDbProvider: DEFAULT_VECTOR_DB_PROVIDER,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const newNetwork = data.network;

          isCreatingNetworkRef.current = true;
          setExecutions([]);
          onTriggerStartDeployment?.(message, newNetwork.networkId, { generateNetworkSkills, generative });

          if (onNetworkUpdate) {
            onNetworkUpdate({
              ...newNetwork,
              networkId: newNetwork.networkId,
              name: newNetwork.name || message.slice(0, 50),
              status: newNetwork.status || 'DRAFT',
              _buildPrompt: message,
            });
          }
        } else {
          const errText = await response.text().catch(() => '');
          dispatch({ type: 'client/SEND_MESSAGE', payload: { id: crypto.randomUUID(), content: `Could not create network (${response.status}). ${errText ? errText.slice(0, 200) : 'Try again or check the API.'}`, role: 'assistant', replace: true, timestamp: new Date().toISOString() } });
        }
      } catch (error) {
        console.error('Error creating network:', error);
        dispatch({ type: 'client/SEND_MESSAGE', payload: { id: crypto.randomUUID(), content: 'Network creation failed (network error). Check your connection and try again.', role: 'assistant', replace: true, timestamp: new Date().toISOString() } });
      }
    } else {
      onTriggerStartDeployment?.(message, network.networkId, { generateNetworkSkills, generative });
    }
  };

  // ─── Approval actions ─────────────────────────────────────────────────

  const handleApprovalDecision = async (decision, taskToken, feedback = null) => {
    try {
      const idToken = getRestApiBearerToken();
      const connectionIdToSend = currentConnectionId || storeConnectionId;

      const response = await fetch(`${API_BASE_URL}/networks/approval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ taskToken, decision, feedback, connectionId: connectionIdToSend }),
      });

      if (response.ok) {
        // State already wiped by the client/APPROVE|DENY|MODIFY dispatch above;
        // clearApproval is a no-op safety net for edge cases (e.g. direct REST call).
        clearApproval({ clearStages: true });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to send approval decision:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error sending approval decision:', error);
    }
  };

  const handleApprove = () => {
    if (!approvalState.taskToken) return;
    dispatch({ type: 'client/APPROVE', payload: { taskToken: approvalState.taskToken } });
    handleApprovalDecision('APPROVE', approvalState.taskToken, null);
  };

  const handleDeny = () => {
    if (!approvalState.taskToken) return;
    dispatch({ type: 'client/DENY', payload: { taskToken: approvalState.taskToken } });
    handleApprovalDecision('DENY', approvalState.taskToken, null);
  };

  const handleModify = () => {
    if (!approvalState.taskToken) return;
    dispatch({ type: 'client/MODIFY', payload: { taskToken: approvalState.taskToken } });
    setTimeout(() => { chatInputRef.current?.focus(); }, 100);
  };

  // ─── Retry approval load ──────────────────────────────────────────────

  const retryApprovalLoad = useCallback(async () => {
    if (!pendingTaskIdForRetry || !network?.networkId) return;
    setApprovalLoadError(false);
    try {
      const details = await fetchExecutionDetails(pendingTaskIdForRetry);
      if (details?.status === 'AWAITING_APPROVAL' && details.taskToken) {
        restoreApproval(details);
      } else {
        setApprovalLoadError(true);
      }
    } catch (e) {
      setApprovalLoadError(true);
    }
  }, [pendingTaskIdForRetry, network?.networkId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render ───────────────────────────────────────────────────────────

  // The kick-start toggle is a build-time choice — only meaningful before the
  // first successful build. Show it for a new/unbuilt network.
  const showSkillToggle = !network
    || network.networkId === 'new'
    || String(network.status || '').toUpperCase() === 'DRAFT';

  // "Revise prompts only" — re-render every agent's prompt from the EXISTING
  // plan with the latest platform rules, skipping the architect Opus re-plan
  // (NETWORK-TO-ROOMS.md §2.2). Only meaningful once the network has a plan to
  // refresh (i.e. NOT a new/unbuilt network). Progress streams via the same
  // AppSync build channel as a normal deploy, so no extra feedback wiring needed.
  const [refreshingPrompts, setRefreshingPrompts] = useState(false);
  const canRefreshPrompts = !!network && network.networkId !== 'new' && !showSkillToggle;
  const handleRefreshPrompts = useCallback(async () => {
    if (!network?.networkId || network.networkId === 'new' || refreshingPrompts) return;
    setRefreshingPrompts(true);
    try {
      sendMessage('↻ Revising agent prompts (no re-plan)…');
      await refreshNetworkPrompts(network.networkId);
    } catch (e) {
      sendMessage(`Prompt refresh failed: ${e?.message || e}`);
    } finally {
      setRefreshingPrompts(false);
    }
  }, [network?.networkId, refreshingPrompts, sendMessage]);

  // Composer "+" popover (left of the input) — extensible per-surface options.
  // Network Workbench offers: Skills (toggle — build-time choice, only for a
  // new/unbuilt network), Revise prompts only (existing networks), + Attachment.
  const plusOptions = [
    ...(showSkillToggle ? [{
      id: 'skills',
      label: 'Skills',
      icon: Sparkles,
      type: 'toggle',
      checked: generateNetworkSkills,
      onToggle: () => setGenerateNetworkSkills((v) => !v),
    }] : []),
    // Generative: route this build to Brain2 (manifest architect + recursive engine).
    // Now the DEFAULT (on) — turn OFF to fall back to the Simple brain (ASL). Build-time
    // choice — only for a new/unbuilt network, same gate as Skills. (§GENERATIVE-NETWORKS.md)
    ...(showSkillToggle ? [{
      id: 'generative',
      label: 'Generative network (default)',
      icon: Wand2,
      type: 'toggle',
      checked: generative,
      onToggle: () => setGenerative((v) => !v),
    }] : []),
    ...(canRefreshPrompts ? [{
      id: 'refresh-prompts',
      label: refreshingPrompts ? 'Revising prompts…' : 'Revise prompts only',
      icon: Wand2,
      type: 'action',
      disabled: refreshingPrompts,
      onClick: handleRefreshPrompts,
    }] : []),
    {
      id: 'attachment',
      label: 'Attachment',
      icon: Paperclip,
      type: 'action',
      disabled: true,  // build-tab attachments not wired yet (coming soon)
      onClick: () => {},
    },
  ];
  const skillToggleSlot = (
    <ComposerPlusMenu testId="build-composer-plus" options={plusOptions} />
  );

  // Which diagram renderer to use. Prefer the LOADED network's ACTUAL engine
  // (``runtimeConfig.engine === 'manifest'`` → the org-tree renderer) so a reload /
  // revisit picks the right one regardless of the local send-toggle. Fall back to the
  // ``generative`` toggle only for a brand-new network still mid-build, before its row
  // (and engine) is persisted. This mirrors AgentsTab, which already derives from the
  // network — BuildTab previously used the raw toggle, so an ASL network reloaded with
  // the toggle left ON would wrongly render through ManifestTreeFlow.
  const networkEngine = String(network?.runtimeConfig?.engine || '').toLowerCase();
  const renderGenerative = networkEngine
    ? networkEngine === 'manifest'
    : generative;

  // DIMMED (capped-out) agents for the network diagram on the BUILD tab — the FIRST graph the user
  // sees on load (before the Agents tab mounts). The stage cap is a saved network-wide "Shared"
  // setting, so we read it straight off ``network.agents.__roomContext__.stageDepthCap`` and dim
  // agents deeper than the cap. Recomputed per network (``network`` dep) so switching networks is
  // dynamic — the disabled stages reflect THIS network's cap immediately, no tab-switch needed.
  // Keyed by logicalId (node ``data.label``) to match ``ManifestTreeFlow``'s ``nodeAgentId``.
  const stageCapDimmedIds = useMemo(() => {
    if (!renderGenerative) return null;
    const rc = (network?.agents && typeof network.agents === 'object')
      ? network.agents.__roomContext__ : null;
    const cap = Math.max(0, Number.parseInt(rc?.stageDepthCap, 10) || 0);
    if (!cap) return null;
    const nodes = Array.isArray(artifact?.nodes) ? artifact.nodes : [];
    const out = new Set();
    for (const n of nodes) {
      const id = String(n?.id || '');
      if (!id.startsWith('root/')) continue;
      const depth = id.split('/').length - 1;          // stage = depth
      if (depth > cap) {
        const lid = String(n?.data?.label || '').trim() || id.split('/').pop();
        if (lid) out.add(lid);
      }
    }
    return out.size ? out : null;
  }, [network, artifact, renderGenerative]);

  return (
    // Split layout mirroring the Agents tab (AgentConfigEditor): chat on the
    // left, the network diagram in a dedicated right-side panel. The diagram is
    // no longer inlined in the chat stream — it lives here, including during
    // approval (the approve/deny controls stay with the composer on the left).
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <ChatWorkspace
          network={network}
          messages={messages}
          stages={stages}
          activeStage={activeStage}
          artifact={artifact}
          isProcessing={isProcessing}
          buildActivity={buildActivity}
          seedText={seedPrompt}
          onSendMessage={handleSendMessage}
          onApprovalDecision={handleApprovalDecision}
          approvalDecisions={approvalDecisions}
          approvalLoadError={approvalLoadError}
          onRetryApprovalLoad={retryApprovalLoad}
          showApprovalBar={showApprovalBar}
          onApprove={handleApprove}
          onDeny={handleDeny}
          onModify={handleModify}
          inputDisabled={inputDisabled}
          inputRef={chatInputRef}
          composerBottomLeft={skillToggleSlot}
        />
      </div>

      {/* Drag handle — adjusts the diagram panel width (lg+ only; below lg the
          panel stacks full-width). Mirrors the Run tab's col-resize grip. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize network diagram panel"
        onMouseDown={onDiagramResizeStart}
        className="group relative hidden w-1.5 shrink-0 cursor-col-resize items-stretch justify-center bg-gray-200/60 hover:bg-blue-500/40 transition-colors lg:flex"
      >
        <div className="my-auto h-10 w-px rounded-full bg-gray-300 group-hover:bg-blue-500/70" />
      </div>

      <aside
        className="flex min-h-[220px] w-full shrink-0 flex-col border-t border-gray-200 bg-gradient-to-br from-gray-50 to-blue-50/40 lg:h-auto lg:min-h-0 lg:w-[var(--diagram-w)] lg:border-l lg:border-t-0"
        style={{ ['--diagram-w']: `${diagramWidth}px` }}
        aria-label="Network diagram"
      >
        <div className="min-h-[200px] flex-1 lg:min-h-0">
          {/* States, by precedence:
              1) Diagram available + not processing → render it (architect
                 proposed → pending approval → final).
              2) Building (isProcessing) → spinner. ONLY a live build shows the
                 spinner — a TERMINAL state (deny / fail / success) flips
                 isProcessing false, so the spinner can never get stuck.
              3) Idle with no diagram (fresh network, or after deny) → placeholder. */}
          {!isProcessing && artifact ? (
            renderGenerative ? (
              /* Generative manifest org-tree → standalone left→right tree view. */
              <ManifestTreeFlow data={artifact} dimmedAgentIds={stageCapDimmedIds} />
            ) : (
              <GraphVisualization data={artifact} stickyResetKey={network?.networkId} />
            )
          ) : isProcessing ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <svg className="h-6 w-6 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-xs text-gray-500">Building the network…</span>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center px-6 text-center text-xs text-gray-400">
              The network diagram appears here once the architect proposes a design.
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
